import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, Calendar, Users, Clock, Plus, Edit2, Trash2, 
  Download, Search, Filter, CheckCircle, XCircle, Eye,
  UserPlus, Save, RefreshCw, ChevronDown, ChevronUp,
  Phone, Mail, Home, FileText, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPantrySystem() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [slots, setSlots] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, slotsRes, regsRes, staffRes] = await Promise.all([
        axios.get(`${API}/pantry/stats`),
        axios.get(`${API}/pantry/slots?include_past=true`),
        axios.get(`${API}/pantry/registrations?limit=500`),
        axios.get(`${API}/pantry/staff/admin`)
      ]);
      
      setStats(statsRes.data);
      setSlots(slotsRes.data.slots || []);
      setRegistrations(regsRes.data.registrations || []);
      setStaff(staffRes.data.staff || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: QrCode },
    { id: 'slots', label: 'Appointment Slots', icon: Calendar },
    { id: 'registrations', label: 'Registrations', icon: Users },
    { id: 'staff', label: 'Staff PINs', icon: UserPlus },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pantry-system">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-serif text-charcoal flex items-center gap-2">
            <QrCode className="text-burgundy" />
            Pantry Check-in System
          </h1>
          <p className="text-charcoal/60 text-sm mt-1">
            Manage appointments, registrations, and staff for the food pantry
          </p>
        </div>
        <button
          onClick={fetchAllData}
          className="flex items-center gap-2 px-4 py-2 bg-charcoal text-white rounded-lg hover:bg-charcoal/90 transition-colors"
          data-testid="refresh-btn"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-charcoal/10 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-b-2 border-burgundy text-burgundy'
                : 'text-charcoal/60 hover:text-charcoal'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'overview' && (
            <OverviewTab stats={stats} slots={slots} registrations={registrations} />
          )}
          {activeTab === 'slots' && (
            <SlotsTab 
              slots={slots} 
              onRefresh={fetchAllData}
              showModal={showSlotModal}
              setShowModal={setShowSlotModal}
              editingSlot={editingSlot}
              setEditingSlot={setEditingSlot}
            />
          )}
          {activeTab === 'registrations' && (
            <RegistrationsTab 
              registrations={registrations}
              dateFilter={dateFilter}
              setDateFilter={setDateFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              showModal={showRegistrationModal}
              setShowModal={setShowRegistrationModal}
              selectedRegistration={selectedRegistration}
              setSelectedRegistration={setSelectedRegistration}
            />
          )}
          {activeTab === 'staff' && (
            <StaffTab 
              staff={staff}
              onRefresh={fetchAllData}
              showModal={showStaffModal}
              setShowModal={setShowStaffModal}
              editingStaff={editingStaff}
              setEditingStaff={setEditingStaff}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ stats, slots, registrations }) {
  const upcomingSlots = slots.filter(s => new Date(s.date) >= new Date()).slice(0, 5);
  const recentRegistrations = registrations.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Today's Appointments" 
          value={stats?.today?.registrations || 0}
          subtitle={`${stats?.today?.checked_in || 0} checked in`}
          icon={Calendar}
          color="burgundy"
        />
        <StatCard 
          title="Pending Check-ins" 
          value={stats?.today?.pending || 0}
          subtitle="Awaiting today"
          icon={Clock}
          color="gold"
        />
        <StatCard 
          title="Total Served" 
          value={stats?.total?.people_served || 0}
          subtitle="People helped"
          icon={Users}
          color="purple"
        />
        <StatCard 
          title="Upcoming Slots" 
          value={stats?.upcoming_slots || 0}
          subtitle="Available for booking"
          icon={QrCode}
          color="charcoal"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Slots */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-burgundy" />
            Upcoming Slots
          </h3>
          {upcomingSlots.length === 0 ? (
            <p className="text-charcoal/50 text-sm">No upcoming slots scheduled</p>
          ) : (
            <div className="space-y-3">
              {upcomingSlots.map((slot) => (
                <div key={slot.id} className="flex justify-between items-center p-3 bg-cream rounded-lg">
                  <div>
                    <p className="font-medium text-charcoal">{slot.date}</p>
                    <p className="text-sm text-charcoal/60">{slot.start_time} - {slot.end_time}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-burgundy">{slot.registered}/{slot.capacity}</p>
                    <p className="text-xs text-charcoal/50">registered</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Registrations */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <Users size={20} className="text-burgundy" />
            Recent Registrations
          </h3>
          {recentRegistrations.length === 0 ? (
            <p className="text-charcoal/50 text-sm">No registrations yet</p>
          ) : (
            <div className="space-y-3">
              {recentRegistrations.map((reg) => (
                <div key={reg.registration_id} className="flex justify-between items-center p-3 bg-cream rounded-lg">
                  <div>
                    <p className="font-medium text-charcoal">{reg.full_name}</p>
                    <p className="text-sm text-charcoal/60">{reg.appointment_date} • {reg.appointment_time}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    reg.checked_in 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {reg.checked_in ? 'Checked In' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, subtitle, icon: Icon, color }) {
  const colors = {
    burgundy: 'bg-burgundy/10 text-burgundy',
    gold: 'bg-gold/20 text-gold',
    purple: 'bg-purple/10 text-purple',
    charcoal: 'bg-charcoal/10 text-charcoal',
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-charcoal/60">{title}</p>
          <p className="text-3xl font-serif text-charcoal mt-1">{value}</p>
          <p className="text-xs text-charcoal/50 mt-1">{subtitle}</p>
        </div>
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

// Slots Tab Component
function SlotsTab({ slots, onRefresh, showModal, setShowModal, editingSlot, setEditingSlot }) {
  const [formData, setFormData] = useState({
    date: '',
    start_time: '09:00',
    end_time: '12:00',
    capacity: 50,
    notes: ''
  });

  useEffect(() => {
    if (editingSlot) {
      setFormData({
        date: editingSlot.date,
        start_time: editingSlot.start_time,
        end_time: editingSlot.end_time,
        capacity: editingSlot.capacity,
        notes: editingSlot.notes || ''
      });
    } else {
      setFormData({
        date: '',
        start_time: '09:00',
        end_time: '12:00',
        capacity: 50,
        notes: ''
      });
    }
  }, [editingSlot]);

  const handleSaveSlot = async () => {
    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      if (editingSlot) {
        await axios.put(`${API}/pantry/slots/${editingSlot.id}`, formData);
        toast.success('Slot updated successfully');
      } else {
        await axios.post(`${API}/pantry/slots`, formData);
        toast.success('Slot created successfully');
      }
      setShowModal(false);
      setEditingSlot(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save slot');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this slot?')) return;
    
    try {
      await axios.delete(`${API}/pantry/slots/${slotId}`);
      toast.success('Slot deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete slot');
    }
  };

  return (
    <div className="space-y-4">
      {/* Add Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            setEditingSlot(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-burgundy text-white rounded-lg hover:bg-burgundy/90 transition-colors"
          data-testid="add-slot-btn"
        >
          <Plus size={18} />
          Add Slot
        </button>
      </div>

      {/* Slots Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Capacity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Registered</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Available</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Notes</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-charcoal/60 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/10">
              {slots.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-charcoal/50">
                    No slots created yet
                  </td>
                </tr>
              ) : (
                slots.map((slot) => (
                  <tr key={slot.id} className="hover:bg-cream/50">
                    <td className="px-4 py-3 font-medium text-charcoal">{slot.date}</td>
                    <td className="px-4 py-3 text-charcoal/70">{slot.start_time} - {slot.end_time}</td>
                    <td className="px-4 py-3 text-charcoal/70">{slot.capacity}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-burgundy/10 text-burgundy rounded-full text-sm">
                        {slot.registered}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        slot.available > 0 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {slot.available}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-charcoal/50 text-sm max-w-xs truncate">{slot.notes || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setEditingSlot(slot);
                            setShowModal(true);
                          }}
                          className="p-2 hover:bg-charcoal/5 rounded-lg transition-colors"
                          data-testid={`edit-slot-${slot.id}`}
                        >
                          <Edit2 size={16} className="text-charcoal/60" />
                        </button>
                        <button
                          onClick={() => handleDeleteSlot(slot.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-slot-${slot.id}`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slot Modal */}
      <AnimatePresence>
        {showModal && (
          <Modal onClose={() => { setShowModal(false); setEditingSlot(null); }}>
            <h2 className="text-xl font-serif text-charcoal mb-4">
              {editingSlot ? 'Edit Slot' : 'Add New Slot'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal/70 mb-1">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
                  data-testid="slot-date-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
                    data-testid="slot-start-time-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-1">End Time</label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
                    data-testid="slot-end-time-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal/70 mb-1">Capacity</label>
                <input
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
                  data-testid="slot-capacity-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal/70 mb-1">Notes (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
                  placeholder="Any special notes for this slot..."
                  data-testid="slot-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => { setShowModal(false); setEditingSlot(null); }}
                  className="px-4 py-2 text-charcoal/60 hover:text-charcoal transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSlot}
                  className="flex items-center gap-2 px-4 py-2 bg-burgundy text-white rounded-lg hover:bg-burgundy/90 transition-colors"
                  data-testid="save-slot-btn"
                >
                  <Save size={16} />
                  {editingSlot ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

// Registrations Tab Component
function RegistrationsTab({ 
  registrations, dateFilter, setDateFilter, statusFilter, setStatusFilter,
  searchTerm, setSearchTerm, showModal, setShowModal, selectedRegistration, setSelectedRegistration
}) {
  const [exporting, setExporting] = useState(false);

  const filteredRegistrations = registrations.filter(reg => {
    const matchesDate = !dateFilter || reg.appointment_date === dateFilter;
    const matchesStatus = !statusFilter || 
      (statusFilter === 'checked_in' && reg.checked_in) ||
      (statusFilter === 'pending' && !reg.checked_in);
    const matchesSearch = !searchTerm || 
      reg.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.phone?.includes(searchTerm) ||
      reg.registration_id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesDate && matchesStatus && matchesSearch;
  });

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFilter) params.append('date_from', dateFilter);
      
      const response = await axios.get(`${API}/pantry/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pantry_registrations_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, phone, or ID..."
              className="w-full pl-10 pr-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
              data-testid="search-registrations"
            />
          </div>
        </div>
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
          data-testid="date-filter"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          <option value="checked_in">Checked In</option>
          <option value="pending">Pending</option>
        </select>
        <button
          onClick={handleExportCSV}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-charcoal text-white rounded-lg hover:bg-charcoal/90 transition-colors disabled:opacity-50"
          data-testid="export-csv-btn"
        >
          <Download size={16} />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Results count */}
      <p className="text-sm text-charcoal/60">
        Showing {filteredRegistrations.length} of {registrations.length} registrations
      </p>

      {/* Registrations Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Household</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-charcoal/60 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/10">
              {filteredRegistrations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-charcoal/50">
                    No registrations found
                  </td>
                </tr>
              ) : (
                filteredRegistrations.map((reg) => (
                  <tr key={reg.registration_id} className="hover:bg-cream/50">
                    <td className="px-4 py-3 font-mono text-sm text-charcoal">{reg.registration_id}</td>
                    <td className="px-4 py-3 font-medium text-charcoal">{reg.full_name}</td>
                    <td className="px-4 py-3 text-charcoal/70">{reg.phone}</td>
                    <td className="px-4 py-3 text-charcoal/70">{reg.household_size}</td>
                    <td className="px-4 py-3 text-charcoal/70">{reg.appointment_date}</td>
                    <td className="px-4 py-3 text-charcoal/70 text-sm">{reg.appointment_time}</td>
                    <td className="px-4 py-3">
                      {reg.checked_in ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                          <CheckCircle size={12} />
                          Checked In
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                          <Clock size={12} />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setSelectedRegistration(reg);
                          setShowModal(true);
                        }}
                        className="p-2 hover:bg-charcoal/5 rounded-lg transition-colors"
                        data-testid={`view-reg-${reg.registration_id}`}
                      >
                        <Eye size={16} className="text-charcoal/60" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Registration Detail Modal */}
      <AnimatePresence>
        {showModal && selectedRegistration && (
          <Modal onClose={() => { setShowModal(false); setSelectedRegistration(null); }}>
            <h2 className="text-xl font-serif text-charcoal mb-4">Registration Details</h2>
            <div className="space-y-4">
              <div className="bg-cream rounded-lg p-4">
                <p className="text-sm text-charcoal/60 mb-1">Registration ID</p>
                <p className="font-mono text-lg text-charcoal">{selectedRegistration.registration_id}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-charcoal/60 mb-1">Full Name</p>
                  <p className="font-medium text-charcoal">{selectedRegistration.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-charcoal/60 mb-1">Household Size</p>
                  <p className="font-medium text-charcoal">{selectedRegistration.household_size} people</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-charcoal/60 mb-1">Phone</p>
                  <p className="font-medium text-charcoal flex items-center gap-2">
                    <Phone size={14} />
                    {selectedRegistration.phone}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-charcoal/60 mb-1">Email</p>
                  <p className="font-medium text-charcoal flex items-center gap-2">
                    <Mail size={14} />
                    {selectedRegistration.email || 'Not provided'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-charcoal/60 mb-1">Appointment Date</p>
                  <p className="font-medium text-charcoal">{selectedRegistration.appointment_date}</p>
                </div>
                <div>
                  <p className="text-sm text-charcoal/60 mb-1">Appointment Time</p>
                  <p className="font-medium text-charcoal">{selectedRegistration.appointment_time}</p>
                </div>
              </div>

              <div className="bg-cream rounded-lg p-4">
                <p className="text-sm text-charcoal/60 mb-2">Check-in Status</p>
                {selectedRegistration.checked_in ? (
                  <div className="space-y-1">
                    <p className="font-medium text-green-700 flex items-center gap-2">
                      <CheckCircle size={16} />
                      Checked In
                    </p>
                    <p className="text-sm text-charcoal/60">
                      By: {selectedRegistration.checked_in_by} at {new Date(selectedRegistration.checked_in_at).toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <p className="font-medium text-yellow-700 flex items-center gap-2">
                    <Clock size={16} />
                    Not yet checked in
                  </p>
                )}
              </div>

              {selectedRegistration.id_photo_url && (
                <div>
                  <p className="text-sm text-charcoal/60 mb-2">ID Photo</p>
                  <img 
                    src={`${API}/pantry/files/${selectedRegistration.id_photo_url}`}
                    alt="ID Photo"
                    className="w-32 h-32 object-cover rounded-lg border border-charcoal/20"
                  />
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => { setShowModal(false); setSelectedRegistration(null); }}
                  className="px-4 py-2 bg-charcoal text-white rounded-lg hover:bg-charcoal/90 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

// Staff Tab Component
function StaffTab({ staff, onRefresh, showModal, setShowModal, editingStaff, setEditingStaff }) {
  const [formData, setFormData] = useState({
    name: '',
    pin: '',
    role: 'volunteer'
  });

  useEffect(() => {
    if (editingStaff) {
      setFormData({
        name: editingStaff.name,
        pin: editingStaff.pin,
        role: editingStaff.role || 'volunteer'
      });
    } else {
      setFormData({
        name: '',
        pin: '',
        role: 'volunteer'
      });
    }
  }, [editingStaff]);

  const handleSaveStaff = async () => {
    if (!formData.name || !formData.pin) {
      toast.error('Name and PIN are required');
      return;
    }

    if (formData.pin.length !== 4 || !/^\d{4}$/.test(formData.pin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }

    try {
      if (editingStaff) {
        await axios.put(`${API}/pantry/staff/${editingStaff.name}/pin`, {
          name: formData.name,
          new_pin: formData.pin
        });
        toast.success('Staff updated successfully');
      } else {
        await axios.post(`${API}/pantry/staff`, formData);
        toast.success('Staff added successfully');
      }
      setShowModal(false);
      setEditingStaff(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff');
    }
  };

  const handleDeleteStaff = async (staffName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${staffName}?`)) return;
    
    try {
      await axios.delete(`${API}/pantry/staff/${staffName}`);
      toast.success('Staff deactivated');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate staff');
    }
  };

  return (
    <div className="space-y-4">
      {/* Info Card */}
      <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="text-gold flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-medium text-charcoal">Staff PINs for Mobile Scanner App</p>
          <p className="text-sm text-charcoal/70 mt-1">
            These 4-digit PINs are used by staff/volunteers to log into the mobile scanner app. 
            Each staff member uses their unique PIN to verify guests and record check-ins.
          </p>
        </div>
      </div>

      {/* Add Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            setEditingStaff(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-burgundy text-white rounded-lg hover:bg-burgundy/90 transition-colors"
          data-testid="add-staff-btn"
        >
          <UserPlus size={18} />
          Add Staff
        </button>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">PIN</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-charcoal/60 uppercase">Total Check-ins</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-charcoal/60 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/10">
              {staff.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-charcoal/50">
                    No staff members yet
                  </td>
                </tr>
              ) : (
                staff.map((member) => (
                  <tr key={member.name} className="hover:bg-cream/50">
                    <td className="px-4 py-3 font-medium text-charcoal">{member.name}</td>
                    <td className="px-4 py-3">
                      <code className="px-2 py-1 bg-charcoal/5 rounded text-charcoal font-mono">
                        {member.pin}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        member.role === 'admin' 
                          ? 'bg-burgundy/10 text-burgundy' 
                          : 'bg-charcoal/10 text-charcoal'
                      }`}>
                        {member.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-charcoal/70">{member.total_checkins}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setEditingStaff(member);
                            setShowModal(true);
                          }}
                          className="p-2 hover:bg-charcoal/5 rounded-lg transition-colors"
                          data-testid={`edit-staff-${member.name}`}
                        >
                          <Edit2 size={16} className="text-charcoal/60" />
                        </button>
                        <button
                          onClick={() => handleDeleteStaff(member.name)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-staff-${member.name}`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Staff Modal */}
      <AnimatePresence>
        {showModal && (
          <Modal onClose={() => { setShowModal(false); setEditingStaff(null); }}>
            <h2 className="text-xl font-serif text-charcoal mb-4">
              {editingStaff ? 'Edit Staff' : 'Add New Staff'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal/70 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Staff member name"
                  className="w-full px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
                  data-testid="staff-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal/70 mb-1">4-Digit PIN</label>
                <input
                  type="text"
                  value={formData.pin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setFormData({ ...formData, pin: val });
                  }}
                  placeholder="1234"
                  maxLength={4}
                  className="w-full px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent font-mono text-center text-2xl tracking-widest"
                  data-testid="staff-pin-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal/70 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 border border-charcoal/20 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
                  data-testid="staff-role-select"
                >
                  <option value="volunteer">Volunteer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => { setShowModal(false); setEditingStaff(null); }}
                  className="px-4 py-2 text-charcoal/60 hover:text-charcoal transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStaff}
                  className="flex items-center gap-2 px-4 py-2 bg-burgundy text-white rounded-lg hover:bg-burgundy/90 transition-colors"
                  data-testid="save-staff-btn"
                >
                  <Save size={16} />
                  {editingStaff ? 'Update' : 'Add Staff'}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

// Modal Component
function Modal({ children, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
